import { useState, useEffect, useRef, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Upload, X, Sun, Moon, Palette, Type, AlertTriangle, CheckCircle2, Save, Eye, Loader2 } from "lucide-react";
import { contrastRatio, wcagLevel, isValidHex, bestForeground } from "@/lib/wcag-contrast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";

const logoSizes = [
  { value: "small", label: "Small", height: "h-8" },
  { value: "medium", label: "Medium", height: "h-12" },
  { value: "large", label: "Large", height: "h-16" },
  { value: "xlarge", label: "Extra Large", height: "h-20" },
];

const fontFamilies = [
  { value: "Inter", label: "Inter" },
  { value: "DM Sans", label: "DM Sans" },
  { value: "Poppins", label: "Poppins" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Lato", label: "Lato" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Nunito", label: "Nunito" },
  { value: "Raleway", label: "Raleway" },
  { value: "Source Sans 3", label: "Source Sans 3" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Merriweather", label: "Merriweather" },
];

const fontSizes = [
  { value: "small", label: "Small (14px)" },
  { value: "medium", label: "Medium (16px)" },
  { value: "large", label: "Large (18px)" },
];

const LogoUpload = ({
  label,
  icon: Icon,
  currentUrl,
  onUpload,
  onRemove,
}: {
  label: string;
  icon: React.ElementType;
  currentUrl: string | null;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { t } = useLanguage();

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </Label>
      {currentUrl ? (
        <div className="flex items-center gap-3">
          <div className="h-20 w-40 rounded-lg border border-border bg-muted/30 flex items-center justify-center p-2">
            <img src={currentUrl} alt={label} className="max-h-full max-w-full object-contain" />
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onRemove}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? t("shopset.uploading") : t("shopset.upload")}
        </Button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
    </div>
  );
};

const ContrastBadge = ({ fg, bg }: { fg: string; bg: string }) => {
  if (!fg || !bg || !isValidHex(fg) || !isValidHex(bg)) return null;
  const ratio = contrastRatio(fg, bg);
  const level = wcagLevel(ratio);
  return (
    <div className={cn(
      "flex items-center gap-1 mt-1 text-[10px] font-medium rounded px-1.5 py-0.5 w-fit",
      level === "fail" ? "bg-destructive/10 text-destructive" : level === "AA" ? "bg-amber-500/10 text-amber-600 dark:text-amber-400" : "bg-green-500/10 text-green-600 dark:text-green-400"
    )}>
      {level === "fail" ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
      {ratio.toFixed(1)}:1 — {level === "fail" ? `Fails WCAG AA (min 4.5:1)` : `Passes WCAG ${level}`}
    </div>
  );
};

const ColorField = ({
  label,
  value,
  onChange,
  placeholder = "#3b82f6",
  contrastWith,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  contrastWith?: string;
}) => (
  <div className="space-y-2">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-9 cursor-pointer rounded border border-border bg-transparent p-0.5"
        aria-label={`Pick ${label}`}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 font-mono text-xs"
        aria-label={label}
      />
      {value && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => onChange("")} aria-label={`Clear ${label}`}>
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
    {contrastWith && value && <ContrastBadge fg={value} bg={contrastWith} />}
  </div>
);

interface ShopStyle {
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  logoSize: string;
  primaryColor: string;
  primaryColorDark: string;
  buttonTextColor: string;
  buttonTextColorDark: string;
  textColor: string;
  textColorDark: string;
  bgColor: string;
  bgColorDark: string;
  cardBgColor: string;
  cardBgColorDark: string;
  fontFamily: string;
  fontSize: string;
}

const defaultStyle: ShopStyle = {
  logoLightUrl: null,
  logoDarkUrl: null,
  logoSize: "medium",
  primaryColor: "",
  primaryColorDark: "",
  buttonTextColor: "",
  buttonTextColorDark: "",
  textColor: "",
  textColorDark: "",
  bgColor: "",
  bgColorDark: "",
  cardBgColor: "",
  cardBgColorDark: "",
  fontFamily: "Inter",
  fontSize: "medium",
};

const ShopPreview = ({ style }: { style: ShopStyle }) => {
  const { t } = useLanguage();
  const bgLight = style.bgColor || "#ffffff";
  const textLight = style.textColor || "#1a1a1a";
  const primaryLight = style.primaryColor || "#3b82f6";
  const bgDark = style.bgColorDark || "#0a0a0a";
  const textDark = style.textColorDark || "#f5f5f5";
  const primaryDark = style.primaryColorDark || "#60a5fa";
  const fontFamily = `"${style.fontFamily}", sans-serif`;
  const fontSize = style.fontSize === "small" ? "12px" : style.fontSize === "large" ? "15px" : "13px";
  const btnFgLight = isValidHex(primaryLight) ? bestForeground(primaryLight) : "#fff";
  const btnFgDark = isValidHex(primaryDark) ? bestForeground(primaryDark) : "#fff";

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Light preview */}
      <div
        className="rounded-lg border border-border p-4 space-y-3"
        style={{ backgroundColor: bgLight, color: textLight, fontFamily, fontSize }}
      >
         <p className="text-[10px] font-medium opacity-50 uppercase tracking-wider">{t("shopset.preview_light")}</p>
        {style.logoLightUrl && (
          <img src={style.logoLightUrl} alt="Logo" className={cn("object-contain", logoSizes.find(s => s.value === style.logoSize)?.height || "h-12")} />
        )}
        <p className="font-semibold">{t("shopset.preview_service")}</p>
        <p style={{ fontSize }} className="opacity-70">{t("shopset.preview_desc")}</p>
        <div
          className="inline-block rounded-md px-3 py-1.5 text-xs font-medium"
          style={{ backgroundColor: primaryLight, color: btnFgLight }}
        >
          {t("shopset.preview_book")}
        </div>
      </div>
      {/* Dark preview */}
      <div
        className="rounded-lg border border-border p-4 space-y-3"
        style={{ backgroundColor: bgDark, color: textDark, fontFamily, fontSize }}
      >
        <p className="text-[10px] font-medium opacity-50 uppercase tracking-wider">{t("shopset.preview_dark")}</p>
        {style.logoDarkUrl && (
          <img src={style.logoDarkUrl} alt="Logo" className={cn("object-contain", logoSizes.find(s => s.value === style.logoSize)?.height || "h-12")} />
        )}
        <p className="font-semibold">{t("shopset.preview_service")}</p>
        <p style={{ fontSize }} className="opacity-70">{t("shopset.preview_desc")}</p>
        <div
          className="inline-block rounded-md px-3 py-1.5 text-xs font-medium"
          style={{ backgroundColor: primaryDark, color: btnFgDark }}
        >
          {t("shopset.preview_book")}
        </div>
      </div>
    </div>
  );
};

const ShopSettings = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [style, setStyle] = useState<ShopStyle>({ ...defaultStyle });
  const [savedStyle, setSavedStyle] = useState<ShopStyle>({ ...defaultStyle });
  const [showPreview, setShowPreview] = useState(false);

  const isDirty = JSON.stringify(style) !== JSON.stringify(savedStyle);

  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.organisation_id) return;
      const { data } = await supabase
        .from("organisations")
        .select("logo_light_url, logo_dark_url, logo_size, shop_primary_color, shop_primary_color_dark, shop_button_text_color, shop_button_text_color_dark, shop_text_color, shop_text_color_dark, shop_bg_color, shop_bg_color_dark, shop_card_bg_color, shop_card_bg_color_dark, shop_font_family, shop_font_size")
        .eq("id", profile.organisation_id)
        .maybeSingle();
      if (data) {
        const d = data as any;
        const loaded: ShopStyle = {
          logoLightUrl: d.logo_light_url || null,
          logoDarkUrl: d.logo_dark_url || null,
          logoSize: d.logo_size || "medium",
          primaryColor: d.shop_primary_color || "",
          primaryColorDark: d.shop_primary_color_dark || "",
          buttonTextColor: d.shop_button_text_color || "",
          buttonTextColorDark: d.shop_button_text_color_dark || "",
          textColor: d.shop_text_color || "",
          textColorDark: d.shop_text_color_dark || "",
          bgColor: d.shop_bg_color || "",
          bgColorDark: d.shop_bg_color_dark || "",
          cardBgColor: d.shop_card_bg_color || "",
          cardBgColorDark: d.shop_card_bg_color_dark || "",
          fontFamily: d.shop_font_family || "Inter",
          fontSize: d.shop_font_size || "medium",
        };
        setStyle(loaded);
        setSavedStyle(loaded);
      }
      setLoading(false);
    };
    fetchData();
  }, [profile?.organisation_id]);

  const handleSave = useCallback(async () => {
    if (!profile?.organisation_id) return;
    setSaving(true);
    const { error } = await supabase
      .from("organisations")
      .update({
        logo_size: style.logoSize,
        shop_primary_color: style.primaryColor || null,
        shop_primary_color_dark: style.primaryColorDark || null,
        shop_button_text_color: style.buttonTextColor || null,
        shop_button_text_color_dark: style.buttonTextColorDark || null,
        shop_text_color: style.textColor || null,
        shop_text_color_dark: style.textColorDark || null,
        shop_bg_color: style.bgColor || null,
        shop_bg_color_dark: style.bgColorDark || null,
        shop_card_bg_color: style.cardBgColor || null,
        shop_card_bg_color_dark: style.cardBgColorDark || null,
        shop_font_family: style.fontFamily,
        shop_font_size: style.fontSize,
      })
      .eq("id", profile.organisation_id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSavedStyle({ ...style });
      toast.success(t("shopset.saved"));
    }
  }, [profile?.organisation_id, style]);

  const uploadLogo = async (file: File, variant: "light" | "dark") => {
    if (!profile?.organisation_id) return;
    const ext = file.name.split(".").pop();
    const path = `${profile.organisation_id}/logo-${variant}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("org-logos")
      .upload(path, file, { upsert: true });
    if (uploadError) { toast.error(uploadError.message); return; }

    const { data: publicUrlData } = supabase.storage.from("org-logos").getPublicUrl(path);
    const url = `${publicUrlData.publicUrl}?t=${Date.now()}`;
    const field = variant === "light" ? "logo_light_url" : "logo_dark_url";

    const { error } = await supabase.from("organisations").update({ [field]: url }).eq("id", profile.organisation_id);
    if (error) { toast.error(error.message); return; }

    const key = variant === "light" ? "logoLightUrl" : "logoDarkUrl";
    setStyle((s) => ({ ...s, [key]: url }));
    setSavedStyle((s) => ({ ...s, [key]: url }));
    toast.success(variant === "light" ? t("shopset.light_logo") + " ✓" : t("shopset.dark_logo") + " ✓");
  };

  const removeLogo = async (variant: "light" | "dark") => {
    if (!profile?.organisation_id) return;
    const field = variant === "light" ? "logo_light_url" : "logo_dark_url";
    const key = variant === "light" ? "logoLightUrl" : "logoDarkUrl";
    const currentUrl = style[key];

    if (currentUrl) {
      const urlPath = currentUrl.split("/org-logos/")[1]?.split("?")[0];
      if (urlPath) await supabase.storage.from("org-logos").remove([decodeURIComponent(urlPath)]);
    }

    const { error } = await supabase.from("organisations").update({ [field]: null }).eq("id", profile.organisation_id);
    if (error) { toast.error(error.message); return; }

    setStyle((s) => ({ ...s, [key]: null }));
    setSavedStyle((s) => ({ ...s, [key]: null }));
    toast.success(t("shopset.logo_removed"));
  };

  const updateLocal = (stateKey: keyof ShopStyle) => (value: string) => {
    setStyle((s) => ({ ...s, [stateKey]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-xl font-bold text-foreground">{t("shopset.title")}</h1>
        <Button
          size="sm"
          className="gap-2 w-full sm:w-auto"
          disabled={!isDirty || saving}
          onClick={handleSave}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {t("shopset.save_changes")}
        </Button>
      </div>

      {/* Logo Section */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-sm font-semibold text-foreground mb-1">{t("shopset.logo")}</h2>
        <p className="text-xs text-muted-foreground mb-5">
          {t("shopset.logo_desc")}
        </p>

        <div className="grid gap-6 md:grid-cols-2 mb-6">
          <LogoUpload label={t("shopset.light_logo")} icon={Sun} currentUrl={style.logoLightUrl} onUpload={(f) => uploadLogo(f, "light")} onRemove={() => removeLogo("light")} />
          <LogoUpload label={t("shopset.dark_logo")} icon={Moon} currentUrl={style.logoDarkUrl} onUpload={(f) => uploadLogo(f, "dark")} onRemove={() => removeLogo("dark")} />
        </div>

        <div className="space-y-2 max-w-xs">
          <Label>{t("shopset.logo_size")}</Label>
          <Select value={style.logoSize} onValueChange={updateLocal("logoSize")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {logoSizes.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

      </section>

      {/* Colors Section */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Palette className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t("shopset.colors")}</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-5">
          {t("shopset.colors_desc")}
        </p>

        <div className="space-y-6">
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
              <Sun className="h-3 w-3" /> {t("shopset.light_mode")}
            </h3>
            <div className="grid gap-4 md:grid-cols-3">
              <ColorField label={t("shopset.btn_color")} value={style.primaryColor} onChange={updateLocal("primaryColor")} placeholder="#3b82f6" contrastWith={style.bgColor || "#ffffff"} />
              <ColorField label={t("shopset.btn_text_color")} value={style.buttonTextColor} onChange={updateLocal("buttonTextColor")} placeholder="#ffffff" contrastWith={style.primaryColor || "#3b82f6"} />
              <ColorField label={t("shopset.text_color")} value={style.textColor} onChange={updateLocal("textColor")} placeholder="#1a1a1a" contrastWith={style.bgColor || "#ffffff"} />
              <ColorField label={t("shopset.bg_color")} value={style.bgColor} onChange={updateLocal("bgColor")} placeholder="#ffffff" contrastWith={style.textColor || "#1a1a1a"} />
              <ColorField label={t("shopset.card_bg")} value={style.cardBgColor} onChange={updateLocal("cardBgColor")} placeholder="#ffffff" contrastWith={style.textColor || "#1a1a1a"} />
            </div>
          </div>

          <div className="border-t border-border pt-5">
            <h3 className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
              <Moon className="h-3 w-3" /> {t("shopset.dark_mode")}
            </h3>
            <div className="grid gap-4 md:grid-cols-3">
              <ColorField label={t("shopset.btn_color")} value={style.primaryColorDark} onChange={updateLocal("primaryColorDark")} placeholder="#60a5fa" contrastWith={style.bgColorDark || "#0a0a0a"} />
              <ColorField label={t("shopset.btn_text_color")} value={style.buttonTextColorDark} onChange={updateLocal("buttonTextColorDark")} placeholder="#ffffff" contrastWith={style.primaryColorDark || "#60a5fa"} />
              <ColorField label={t("shopset.text_color")} value={style.textColorDark} onChange={updateLocal("textColorDark")} placeholder="#f5f5f5" contrastWith={style.bgColorDark || "#0a0a0a"} />
              <ColorField label={t("shopset.bg_color")} value={style.bgColorDark} onChange={updateLocal("bgColorDark")} placeholder="#0a0a0a" contrastWith={style.textColorDark || "#f5f5f5"} />
              <ColorField label={t("shopset.card_bg")} value={style.cardBgColorDark} onChange={updateLocal("cardBgColorDark")} placeholder="#1a1a1a" contrastWith={style.textColorDark || "#f5f5f5"} />
            </div>
          </div>
        </div>
      </section>

      {/* Typography Section */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <div className="flex items-center gap-2 mb-1">
          <Type className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t("shopset.typography")}</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-5">
          {t("shopset.typography_desc")}
        </p>

        <div className="grid gap-4 md:grid-cols-2 max-w-lg">
          <div className="space-y-2">
            <Label>{t("shopset.font_family")}</Label>
            <Select value={style.fontFamily} onValueChange={updateLocal("fontFamily")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {fontFamilies.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    <span style={{ fontFamily: f.value }}>{f.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("shopset.font_size")}</Label>
            <Select value={style.fontSize} onValueChange={updateLocal("fontSize")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {fontSizes.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

      </section>

      {/* Sticky save bar when dirty */}
      {isDirty && (
        <div className="sticky bottom-4 mt-6 flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-lg z-50">
          <p className="text-sm text-muted-foreground">{t("shopset.unsaved")}</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setStyle({ ...savedStyle })}>
              {t("shopset.discard")}
            </Button>
            <Button size="sm" className="gap-2" disabled={saving} onClick={handleSave}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {t("shopset.save_changes")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default ShopSettings;
